import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCompany } from '../queryKeys';

import { hubspotImportApply } from './hubspotImportApply';
import {
  type HubspotImportApplyMutationOptions,
  type HubspotImportApplyVariables,
} from './types';

/**
 * Mutation hook for applying the supplier assignments confirmed after a HubSpot
 * import.
 *
 * Invalidates the whole company domain: one request touches the relationship of
 * an arbitrary number of supplier companies, so the affected cache entries are
 * not knowable from the variables.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin---Companies/operation/post_hubspot_import_apply_vadmin_company__company_id__hubspot_import_apply_post
 */
export const useMutationHubspotImportApply = (
  options?: HubspotImportApplyMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignments,
      companyId,
    }: HubspotImportApplyVariables) =>
      hubspotImportApply(companyId, assignments),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: [queryKeysCompany.base()],
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
